import { BrandLogo } from "@/components/BrandLogo";
import Empty from "@/components/Empty";
import { NavigationItemWithSubmenu } from "@/components/layout/components/NavigationItemWithSubmenu";
import { NavigationItems } from "@/components/layout/components/NavigationItems";
import UserDropDown from "@/components/layout/components/UserDropDown";
import SearchInput from "@/components/SearchInput";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarRail,
  useSidebar,
} from "@/components/ui/sidebar";
import { TooltipProvider } from "@/components/ui/tooltip";
import {
  BarChart3,
  BookA,
  FileText,
  Folder,
  Network,
  Settings,
  Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { NAVIGATION_GROUP, useAppNavigation } from "../hooks/useAppNavigation";
import ImpersonationBanner from "./ImpersonationBanner";

export default function AppSidebar() {
  const { t } = useTranslation();
  const navigation = useAppNavigation();
  const { open } = useSidebar();

  return (
    <Sidebar collapsible="icon" variant="sidebar" className="z-40">
      <SidebarHeader className="grow-0 shrink-0">
        <SidebarMenuButton asChild>
          <BrandLogo className="h-12" showTitle={open} />
        </SidebarMenuButton>

        {open && (
          <SearchInput
            placeholder={t("common.search")}
            onChange={navigation.onSearchChange}
            value={navigation.searchQuery}
          />
        )}
      </SidebarHeader>
      <SidebarContent className="flex-1 overflow-auto">
        <Empty
          when={navigation.searchResults.hasEmptySearchResults()}
          iconClassName="w-8 h-8"
          className="text-sm gap-2"
        >
          {t("navigation.noItems")}
        </Empty>
        <TooltipProvider delayDuration={100}>
          {navigation.searchResults.hasGroupItems(NAVIGATION_GROUP.MAIN) && (
            <SidebarGroup>
              <SidebarMenu>
                <NavigationItems
                  items={
                    navigation.searchResults.getGroupItems(
                      NAVIGATION_GROUP.MAIN,
                    ) || []
                  }
                />
              </SidebarMenu>
            </SidebarGroup>
          )}
          {navigation.searchResults.hasGroupItems(NAVIGATION_GROUP.USERS) && (
            <SidebarGroup>
              <NavigationItemWithSubmenu
                title={t("navigation.users")}
                icon={Users}
                items={
                  navigation.searchResults.getGroupItems(
                    NAVIGATION_GROUP.USERS,
                  ) || []
                }
                open={navigation.expanded[NAVIGATION_GROUP.USERS]}
                onOpenChange={(isOpen) =>
                  navigation.onOpenChange(NAVIGATION_GROUP.USERS, isOpen)
                }
              />
            </SidebarGroup>
          )}
          {navigation.searchResults.hasGroupItems(
            NAVIGATION_GROUP.PROJECTS,
          ) && (
            <SidebarGroup>
              <NavigationItemWithSubmenu
                title={t("navigation.projectManagement")}
                icon={Folder}
                items={
                  navigation.searchResults.getGroupItems(
                    NAVIGATION_GROUP.PROJECTS,
                  ) || []
                }
                open={navigation.expanded[NAVIGATION_GROUP.PROJECTS]}
                onOpenChange={(isOpen) =>
                  navigation.onOpenChange(NAVIGATION_GROUP.PROJECTS, isOpen)
                }
              />
            </SidebarGroup>
          )}
          {navigation.searchResults.hasGroupItems(
            NAVIGATION_GROUP.PAYROLLS,
          ) && (
            <SidebarGroup>
              <NavigationItemWithSubmenu
                title={t("navigation.payrolls")}
                icon={Folder}
                items={
                  navigation.searchResults.getGroupItems(
                    NAVIGATION_GROUP.PAYROLLS,
                  ) || []
                }
                open={navigation.expanded[NAVIGATION_GROUP.PAYROLLS]}
                onOpenChange={(isOpen) =>
                  navigation.onOpenChange(NAVIGATION_GROUP.PAYROLLS, isOpen)
                }
              />
            </SidebarGroup>
          )}
          {navigation.searchResults.hasGroupItems(NAVIGATION_GROUP.REPORTS) && (
            <SidebarGroup>
              <NavigationItemWithSubmenu
                title={t("navigation.reports")}
                icon={BarChart3}
                items={
                  navigation.searchResults.getGroupItems(
                    NAVIGATION_GROUP.REPORTS,
                  ) || []
                }
                open={navigation.expanded[NAVIGATION_GROUP.REPORTS]}
                onOpenChange={(isOpen) =>
                  navigation.onOpenChange(NAVIGATION_GROUP.REPORTS, isOpen)
                }
              />
            </SidebarGroup>
          )}
          {navigation.searchResults.hasGroupItems(
            NAVIGATION_GROUP.AGREEMENT_MANAGEMENT,
          ) && (
            <SidebarGroup>
              <NavigationItemWithSubmenu
                title={t("navigation.agreementManagement")}
                icon={FileText}
                items={
                  navigation.searchResults.getGroupItems(
                    NAVIGATION_GROUP.AGREEMENT_MANAGEMENT,
                  ) || []
                }
                open={
                  navigation.expanded[NAVIGATION_GROUP.AGREEMENT_MANAGEMENT]
                }
                onOpenChange={(isOpen) =>
                  navigation.onOpenChange(
                    NAVIGATION_GROUP.AGREEMENT_MANAGEMENT,
                    isOpen,
                  )
                }
              />
            </SidebarGroup>
          )}
          {navigation.searchResults.hasGroupItems(
            NAVIGATION_GROUP.DEPARTMENT_BASED_PROJECTS,
          ) && (
            <SidebarGroup>
              <NavigationItemWithSubmenu
                title={t("navigation.departmentBasedProjectManagement")}
                icon={Folder}
                items={
                  navigation.searchResults.getGroupItems(
                    NAVIGATION_GROUP.DEPARTMENT_BASED_PROJECTS,
                  ) || []
                }
                open={
                  navigation.expanded[
                    NAVIGATION_GROUP.DEPARTMENT_BASED_PROJECTS
                  ]
                }
                onOpenChange={(isOpen) =>
                  navigation.onOpenChange(
                    NAVIGATION_GROUP.DEPARTMENT_BASED_PROJECTS,
                    isOpen,
                  )
                }
              />
            </SidebarGroup>
          )}
          {navigation.searchResults.hasGroupItems(
            NAVIGATION_GROUP.STRUCTURE_ASSIGNMENTS,
          ) && (
            <SidebarGroup>
              <NavigationItemWithSubmenu
                title={t("navigation.structureAssignments")}
                icon={Network}
                items={
                  navigation.searchResults.getGroupItems(
                    NAVIGATION_GROUP.STRUCTURE_ASSIGNMENTS,
                  ) || []
                }
                open={
                  navigation.expanded[NAVIGATION_GROUP.STRUCTURE_ASSIGNMENTS]
                }
                onOpenChange={(isOpen) =>
                  navigation.onOpenChange(
                    NAVIGATION_GROUP.STRUCTURE_ASSIGNMENTS,
                    isOpen,
                  )
                }
              />
            </SidebarGroup>
          )}
          {navigation.searchResults.hasGroupItems(
            NAVIGATION_GROUP.ADMINISTRATION,
          ) && (
            <SidebarGroup>
              <NavigationItemWithSubmenu
                title={t("navigation.administration")}
                icon={Settings}
                items={
                  navigation.searchResults.getGroupItems(
                    NAVIGATION_GROUP.ADMINISTRATION,
                  ) || []
                }
                open={navigation.expanded[NAVIGATION_GROUP.ADMINISTRATION]}
                onOpenChange={(isOpen) =>
                  navigation.onOpenChange(
                    NAVIGATION_GROUP.ADMINISTRATION,
                    isOpen,
                  )
                }
              />
            </SidebarGroup>
          )}
          {navigation.searchResults.hasGroupItems(
            NAVIGATION_GROUP.DICTIONARIES,
          ) && (
            <SidebarGroup>
              <NavigationItemWithSubmenu
                title={t("navigation.dictionaries")}
                icon={BookA}
                items={
                  navigation.searchResults.getGroupItems(
                    NAVIGATION_GROUP.DICTIONARIES,
                  ) || []
                }
                open={navigation.expanded[NAVIGATION_GROUP.DICTIONARIES]}
                onOpenChange={(isOpen) =>
                  navigation.onOpenChange(NAVIGATION_GROUP.DICTIONARIES, isOpen)
                }
              />
            </SidebarGroup>
          )}
        </TooltipProvider>
      </SidebarContent>
      <SidebarFooter className="grow-0 shrink-0">
        <ImpersonationBanner onlyIcon={!open} />
        <UserDropDown />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
